import { useLocation, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import {
  Breadcrumb,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import styled from "styled-components";
import { formattedStringIsValidDate, formatDateString } from '../../../util/dates';
import useModalState from "../../../hooks/useModalState";
import useImpersonationForm from './useImpersonationForm'
import useCreateImpersonationRightMutation from './useCreateImpersonationRightMutation';
import useTerminateImpersonationRightsMutation from './useTerminateImpersonationRightsMutation'
import ImpersonationModalWithState from "./ImpersonationModalWithState";
import ActivitiesSectionWithState from "./ActivitiesSectionWithState";
import SyncSectionWithState from "./SyncSectionWithState";
import useFetchImpersonationRights from './useFetchImpersonationRights';
import TenantDetailsSubscriptionSectionWithState from "./TenantDetailsSubscriptionSectionWithState";
import useNotifications from "../../../hooks/useNotifications";
import PageWithState from "../../layout/PageWithState";
// import Date from "../../util/Date";
import {
  Active,
  ContentContainer,
  ContentTitle,
  Disabled,
} from "../tenants/TenantsPage";

const TenantDetailsPage = ({
  tenant,
  allProducts,
  syncLogSummaries,
  analyticsPerDay,
  statisticsPerPartner,
  statisticsPerDate,
  analyticsKeys,
  refetchQueries,
  isUploadingContacts,
  isUploadingCompanies,
  isUploadingProjects,
  isRevertingImport,
  isResettingAccountState,
  onResetAccountState,
  onUploadContacts,
  onUploadCompanies,
  onUploadProjects,
  onRevertImport,
  onDownloadContactsTemplate,
  onDownloadCompaniesTemplate,
  onDownloadProjectsTemplate,
  onDeleteTenant,
  onUpdateSubscription,
  ...props
}) => {
  const navigate = useNavigate();
  const formState  = useImpersonationForm();
  console.log('formState in TenantDetailsPage', formState)
  const { getInputProps } = formState;
  const [createImpersonationRight] = useCreateImpersonationRightMutation()
  const [terminateImpersonationRights] =
        useTerminateImpersonationRightsMutation()
  
  const {
    impersonationRights,
    refetchQueries: refetchImpersonationRights,
    // isFetching,
} = useFetchImpersonationRights({
    tenantGuid: tenant.guid,
    where: { 
        isExpired: false,
        },
    skip: !tenant.guid, // saute la requête si pas d'IDs
  })

  const { dispatchWarning, dispatchSuccess } = useNotifications();

  const getFile = (elementId) => {
    const element = document.getElementById(elementId);
    if (element === null || typeof element.files[0] === "undefined")
      return false;
    return element.files[0];
  };

  const [deleteText, setDeleteText] = useState("");
  const [deleteModal, showDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);  

  const url = useLocation()
    const queryParameters = new URLSearchParams(url.search)
    const expirationDate = queryParameters.get('impersonation-expiration')
  
  const parsedDate = formattedStringIsValidDate(expirationDate)
  const isValidExpirationDate =
        parsedDate !== 'Invalid Date' && parsedDate > new Date()
  
  const impersonationModalState = useModalState({
            expirationDate: isValidExpirationDate
                ? parsedDate
                : getInputProps('expirationDate').value,
        },
        !!expirationDate,
    )

  const revokeImpersonationRights = async (userId) => {
    try {
          await terminateImpersonationRights({
            variables: { keys: [userId] },
            refetchQueries: () => refetchImpersonationRights,
          })
            dispatchSuccess("Success!");
        } catch (e) {
          console.error('Error revoking impersonation right', e)
          dispatchWarning("Something went wrong!");
        }
  }

  const displayImpersonationRights = (userId) => {
    const found = impersonationRights.find(item => item.puppetUserId === userId);
    return (
      <>
       {found ?
          <ImpersonationOverview key={userId}>
                        <span> { formatDateString(found.expirationDateTime)}</span>
                        <Button
                          variant="danger"
                          size="sm"
                          style={{ marginLeft: '20px' }}
                          onClick={() => revokeImpersonationRights(found.key)}
                        >
                          Disable remote support
                        </Button>
                      </ImpersonationOverview>  : 
                      <div className="d-md-flex justify-content-md-start">
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => impersonationModalState.open({userId : userId})}
                        >
                        Impersonation
                      </Button>
                        </div>
       }
      </>
   )
 }
  
  return (
    <>
    <PageWithState {...props}>
      <Breadcrumb>
        <Breadcrumb.Item onClick={() => navigate("/tenants")}>
          Tenants
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{tenant.name}</Breadcrumb.Item>
      </Breadcrumb>
      <ContentContainer fluid>
        <ContentTitle>Overview</ContentTitle>
        <Form>
          <Form.Label>Name</Form.Label>
          <StyledInput disabled size="sm" type="text" value={tenant.name} />

          <Row>
            <Col>
              <Form.Label>ID</Form.Label>
              <StyledInput disabled size="sm" type="text" value={tenant.id} />
            </Col>
            <Col>
              <Form.Label>GUID</Form.Label>
              <StyledInput disabled size="sm" type="text" value={tenant.guid} />
            </Col>
          </Row>
        </Form>
      </ContentContainer>
      <ContentContainer fluid>
        <SyncSectionWithState
          syncLogSummaries={syncLogSummaries}
          analyticsPerDay={analyticsPerDay}
          analyticsKeys={analyticsKeys}
        />
      </ContentContainer>
      <ContentContainer fluid>
        <ActivitiesSectionWithState
          statisticsPerPartner={statisticsPerPartner}
          statisticsPerDate={statisticsPerDate}
        />
      </ContentContainer>
      <ContentContainer fluid>
        <ContentTitle>Users</ContentTitle>
        <Table responsive="lg" hover borderless size="sm">
          <thead>
            <tr>
              <th>UserId</th>
              <th>First name</th>
              <th>Name</th>
              <th>Login</th>
              <th>Terms and conditions</th>
              <th colSpan={2}>Account state</th>
              <th colSpan={2}>Impersonation</th>
            </tr>
          </thead>
          <tbody>
            {tenant.tenantUsers.map((tenantUser, index) => (
              <tr key={tenantUser.user.id}>
                <td>{tenantUser.user.id}</td>
                <td>{tenantUser.user.signUpFirstName}</td>
                <td>{tenantUser.user.signUpName}</td>
                <td>{tenantUser.user.loginEmail}</td>
                <td>{tenantUser.user.termsAndConditionsAcceptedOn}</td>
                <td>{tenantUser.accountStateSlug}</td>
                <td>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => onResetAccountState(tenantUser.user.id)}
                  >
                    {isResettingAccountState === tenantUser.user.id ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Resetting...
                      </>
                    ) : (
                      "Reset"
                    )}
                  </Button>
                </td>
                <td>
                { displayImpersonationRights(tenantUser.user.id)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table> 
        </ContentContainer>     
      <ContentContainer fluid>
        <ContentTitle>Sync</ContentTitle>
        {tenant.isSentMailsSyncActive ? (
          <Active label="mail" />
        ) : (
          <Disabled label="mail" />
        )}{" "}
        {tenant.isCalendarSyncActive ? (
          <Active label="calendar" />
        ) : (
          <Disabled label="calendar" />
        )}{" "}
        {tenant.isCallRecordSyncActive ? (
          <Active label="phone" />
        ) : (
          <Disabled label="phone" />
        )}
      </ContentContainer>
      <ContentContainer fluid>
        <ContentTitle>Stripe</ContentTitle>
        {tenant.subscription ? (
          <TenantDetailsSubscriptionSectionWithState
            allProducts={allProducts}
            stripeCustomerId={tenant.stripeCustomerId}
            subscription={tenant.subscription}
            currentPaymentMethod={tenant.currentPaymentMethod}
            onUpdateSubscription={onUpdateSubscription}
            isDemo={tenant.isDemo}
          />
        ) : (
          <p>No subscription found</p>
        )}
      </ContentContainer>
      <ContentContainer fluid>
        <ContentTitle>Import</ContentTitle>
        <Form>
          <Row>
            <Col>
              <Form.Control type="file" id="contacts-file" />
              <UploadButton
                onClick={onDownloadContactsTemplate}
                size="sm"
                variant="light"
              >
                Template
              </UploadButton>
              <UploadButton
                size="sm"
                id="contacts-upload-button"
                onClick={() => {
                  if (getFile("contacts-file")) {
                    onUploadContacts(tenant.guid, getFile("contacts-file"));
                  } else {
                    dispatchWarning("Please select a file");
                  }
                }}
                disabled={isUploadingContacts}
              >
                {isUploadingContacts ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Uploading contacts...
                  </>
                ) : (
                  "Upload contacts"
                )}
              </UploadButton>
            </Col>
            <Col>
              <Form.Control type="file" id="companies-file" />
              <UploadButton
                onClick={onDownloadCompaniesTemplate}
                size="sm"
                variant="light"
              >
                Template
              </UploadButton>
              <UploadButton
                size="sm"
                onClick={() => {
                  if (getFile("companies-file")) {
                    onUploadCompanies(tenant.guid, getFile("companies-file"));
                  } else {
                    dispatchWarning("Please select a file");
                  }
                }}
                disabled={isUploadingCompanies}
              >
                {isUploadingCompanies ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Uploading companies...
                  </>
                ) : (
                  "Upload companies"
                )}
              </UploadButton>
            </Col>
            <Col>
              <Form.Control type="file" id="projects-file" />
              <UploadButton
                onClick={onDownloadProjectsTemplate}
                size="sm"
                variant="light"
              >
                Template
              </UploadButton>
              <UploadButton
                size="sm"
                onClick={() => {
                  if (getFile("projects-file")) {
                    onUploadProjects(tenant.guid, getFile("projects-file"));
                  } else {
                    dispatchWarning("Please select a file");
                  }
                }}
                disabled={isUploadingProjects}
              >
                {isUploadingProjects ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Uploading projects...
                  </>
                ) : (
                  "Upload projects"
                )}
              </UploadButton>
            </Col>
          </Row>
        </Form>
      </ContentContainer>
      {tenant.dataImports.length > 0 && (
        <ContentContainer fluid>
          <ContentTitle>Import history</ContentTitle>
          <Table responsive="lg" hover borderless size="sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Filename</th>
                <th>createdOn</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {tenant.dataImports.map((dataImport, index) => (
                <tr key={dataImport.id}>
                  <td>{index + 1}</td>
                  <td>{dataImport.fileName}</td>
                  <td>{dataImport.createdOn}</td>
                  <td>
                    {dataImport.reverted ? (
                      "Reverted"
                    ) : (
                      <Button
                        onClick={() => onRevertImport(dataImport.guid)}
                        size="sm"
                        variant="warning"
                      >
                        {isRevertingImport === dataImport.guid ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            Reverting...
                          </>
                        ) : (
                          "Revert"
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ContentContainer>
      )}
      <ContentContainer fluid>
        <ContentTitle>Delete tenant</ContentTitle>
        <Form.Label>Type in the tenant GUID to delete this tenant</Form.Label>
        <StyledInput
          size="sm"
          type="text"
          value={deleteText}
          onChange={(e) => setDeleteText(e.target.value)}
        />
        <Button
          variant="danger"
          disabled={deleteText !== tenant.guid}
          onClick={() => showDeleteModal(true)}
        >
          Delete
        </Button>
      </ContentContainer>
      <Modal show={deleteModal} onHide={() => showDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <b>{tenant.name}</b>?
          </p>
          <p>This can take some time to complete...</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => showDeleteModal(false)}>
            NO - Close
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              onDeleteTenant();
              showDeleteModal(false);
            }}
          >
            YES - Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </PageWithState>
    {impersonationModalState.isOpen && (
      <ImpersonationModalWithState
        onClose={() => impersonationModalState.close()}
        formState={formState}
        onSubmit={async (userId) => {
              setIsSubmitting(true)
                try {
                    const input = {...formState.valuesToInput(), userId, tenantGuid: tenant.guid}
                    console.log('Impersonation input', input)
                    await createImpersonationRight({
                        variables: input,
                        refetchQueries: () => refetchImpersonationRights,
                    })
                    await refetchImpersonationRights()
                    dispatchSuccess("Success!");
                     impersonationModalState.close()
                } catch (e) {
                    console.error('Error creating impersonation right', e)
                    impersonationModalState.close()
                } finally {
                    setIsSubmitting(false)
                }
            }}
            isSubmitting={isSubmitting}
            {...impersonationModalState.initialState}
      />
    )}
  </>
  );
};

const StyledInput = styled(Form.Control)(
  ({ theme }) => `
    margin-bottom: ${theme.spacingSmaller}rem;
`,
);

const UploadButton = styled(Button)(
  ({ theme }) => `
    margin-top: ${theme.spacingSmaller}rem;
    margin-right: ${theme.spacingSmaller}rem;
`,
);

const ImpersonationOverview = styled.div(
    ()=> `
    display: flex;
    justify-content: flex-start;
    align-items: center;
`,
)

export default TenantDetailsPage;
